import { useCallback, useEffect, useMemo, useState } from "react";
import type { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import {
  ChangeSettingsRequestSchema,
  type ChangeSettingsRequest,
  type ConfigurationScopeId,
  type HolderConfiguration,
  type OrganisationId,
  type SettingChange,
} from "@thalamos/haven-common";

import type { ErrorBody, ResponseBody } from "../../api/axios/api.js";
import api from "../../api/index.js";

type HolderResponse = AxiosResponse<
  ResponseBody<HolderConfiguration, ErrorBody>
>;

/**
 * Which arm of `ResponseBody` arrived, decided by the status. Narrowed here, once,
 * so the two readers below say what they got rather than each asserting it.
 */
const settled = (
  response: HolderResponse,
):
  | { ok: true; configuration: HolderConfiguration }
  | { ok: false; failure: ErrorBody } =>
  response.status === 200
    ? { ok: true, configuration: response.data as HolderConfiguration }
    : { ok: false, failure: response.data as ErrorBody };

/**
 * What a caller can do to one holder's settings.
 *
 * Deliberately not typed to a scope or an organisation. The two differ only in
 * which endpoints they call, so passing those in keeps one editor and one set of
 * behaviours for both — a second copy would be where the two drift.
 */
export type HolderEndpoints = {
  read: () => Promise<HolderResponse>;
  change: (request: ChangeSettingsRequest) => Promise<HolderResponse>;
};

/**
 * The reason a write was refused, as the server stated it.
 *
 * Narrowing and the relations between settings are both refused with a message
 * naming the setting and what it inherits, and that message is the whole point of
 * refusing rather than clamping silently. A generic failure here would throw away
 * the only thing the refusal was for.
 */
const refusalReason = (body: ErrorBody, fallback: string): string =>
  body.message ?? body.reason ?? fallback;

/**
 * Loads a holder's configuration and applies changes to it.
 *
 * A save answers with the holder's whole configuration, and that reply is what
 * the page then shows — rather than the page updating the rows it changed. One
 * setting can change what another resolves to, so anything assembled locally
 * would be the fold implemented a second time in the browser.
 */
export const useHolderConfiguration = (endpoints: HolderEndpoints) => {
  const { t } = useTranslation();
  // Held with the read that produced it, so a holder never shows another's
  // configuration, or its own from before a reload, while the next is loading.
  const [loaded, setLoaded] = useState<{
    read: HolderEndpoints["read"];
    readCount: number;
    configuration: HolderConfiguration;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped to read again when something other than a save changed what the
  // holder inherits, such as moving it under another configuration.
  const [readCount, setReadCount] = useState(0);

  const { read } = endpoints;
  const configuration =
    loaded?.read === read && loaded.readCount === readCount
      ? loaded.configuration
      : null;

  useEffect(() => {
    let current = true;

    setLoading(true);
    setError(null);

    read()
      .then((response) => {
        if (!current) {
          return;
        }

        const outcome = settled(response);

        if (outcome.ok) {
          setLoaded({ read, readCount, configuration: outcome.configuration });
        } else {
          setError(t("admin.configuration.settings.loadFailed"));
        }
      })
      .catch(() => {
        if (current) {
          setError(t("admin.configuration.settings.loadFailed"));
        }
      })
      .finally(() => {
        if (current) {
          setLoading(false);
        }
      });

    // A second holder chosen while the first is in flight must not have its
    // answer overwritten by the one that arrives late.
    return () => {
      current = false;
    };
  }, [read, t, readCount]);

  /** Whether the changes were saved; a refusal is left in `error`. */
  const change = useCallback(
    async (changes: readonly SettingChange[]): Promise<boolean> => {
      const request = ChangeSettingsRequestSchema.safeParse({ changes });

      // Unreachable while the panel matches the schema: Save is offered only
      // with changes, the confirmation is modal, changes are keyed by setting
      // and the editor builds valid ones. Kept so a drift fails politely.
      /* v8 ignore if */
      if (!request.success) {
        setError(
          t("admin.configuration.settings.saveFailed", {
            reason: t(
              request.error.issues[0]?.message ??
                "admin.configuration.settings.loadFailed",
            ),
          }),
        );
        return false;
      }

      setSaving(true);

      try {
        const outcome = settled(await endpoints.change(request.data));

        if (outcome.ok) {
          setLoaded({
            read: endpoints.read,
            readCount,
            configuration: outcome.configuration,
          });
          setError(null);
          return true;
        }

        setError(
          t("admin.configuration.settings.saveFailed", {
            reason: refusalReason(
              outcome.failure,
              t("admin.configuration.settings.loadFailed"),
            ),
          }),
        );
        return false;
      } catch {
        setError(t("admin.configuration.settings.loadFailed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [endpoints, readCount, t],
  );

  const reload = useCallback(() => {
    setReadCount((count) => count + 1);
  }, []);

  return { configuration, loading, saving, error, change, reload };
};

/**
 * The endpoints for one level of the chain: read by the id the address names,
 * and changed under the id the server listed, undefined until the list is read.
 *
 * `read` is memoised on the address alone, because it is a dependency of the
 * effect that loads: rebuilt when the list arrives, or on every render, it
 * would read again.
 */
export const useScopeEndpoints = ({
  requestedId,
  listedScopeId,
}: {
  requestedId: string;
  listedScopeId: ConfigurationScopeId | undefined;
}): HolderEndpoints => {
  const read = useCallback(
    () => api.configuration.scopeSettings(requestedId),
    [requestedId],
  );

  return useMemo(
    () => ({
      read,
      change: (request) => {
        // Unreachable: the settings panel is drawn only once the configuration
        // is found among the listed scopes, which is when `listedScopeId` is
        // defined. Kept to fail loudly if not.
        /* v8 ignore if */
        if (listedScopeId === undefined) {
          throw new Error(
            "Expected settings to change only once the configuration is listed",
          );
        }

        return api.configuration.changeScopeSettings(listedScopeId, request);
      },
    }),
    [read, listedScopeId],
  );
};

/**
 * The endpoints for one organisation: a new object each call, so memoise it
 * where it feeds `useHolderConfiguration`.
 */
export const organisationEndpoints = (
  organisationId: OrganisationId,
): HolderEndpoints => ({
  read: () => api.configuration.organisationSettings(organisationId),
  change: (request) =>
    api.configuration.changeOrganisationSettings(organisationId, request),
});
